import Link from 'next/link';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { externalUrls, routes, socialUrls } from '@/constants/urls';
import { cn } from '@/utils/styles';
import useResize from '@/hooks/useResize';
import HivemapperLogo from '@/public/images/logos/hivemapper-logo.svg';
import { Button, ButtonProps } from '../shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../shadcn/dropdown-menu';
import DoubleChevronDown from '../ui/icons/DoubleChevronDown';
import XIcon from '../ui/icons/XIcon';
import { Dialog, DialogOverlay } from '../shadcn/dialog';

import HamburgerIcon from '../ui/icons/HamburgerIcon';
import { P } from '../shadcn/typography';
import HeaderContainer from './HeaderContainer';
interface NavSection {
  title: string;
  items: NavLink[];
}

interface HeaderProps {
  addon?: React.ReactNode;
}

interface MobileHeaderProps {
  isNavOpen: boolean;
  setIsNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface NavLink {
  label: string;
  href: string;
  variant?: ButtonProps['variant'];
  icon?: React.ReactNode;
  newTab?: boolean;
}

const navLinks: NavLink[] = [
  {
    label: 'Coverage',
    href: routes.coverage,
  },
  {
    label: 'Regions',
    href: routes.network.regions,
  },
];

const menuLinks: NavLink[] = [
  {
    label: 'Documentation',
    href: externalUrls.hivemapperDocs,
    newTab: true,
  },
  {
    label: 'Contributors',
    href: routes.network.contributors,
  },
  {
    label: 'Blog',
    href: externalUrls.mediumBlog,
    newTab: true,
  },
  {
    label: 'Privacy Policy',
    href: routes.privacy.privacyPolicy,
  },
  {
    label: 'Terms of Service',
    href: routes.tos,
  },
];

export default function Header({ addon }: HeaderProps) {
  const headerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);

  const { width } = useResize();

  useEffect(() => {
    if (isNavOpen) {
      return;
    }
    const headerHeight = headerRef.current?.offsetHeight || 0;
    document.documentElement.style.setProperty(
      '--header-height',
      `${headerHeight}px`,
    );
  }, [headerRef.current, width]);

  return (
    <div className="fixed z-[51] w-full p-3 md:px-0 md:py-10" ref={headerRef}>
      <header
        className={cn(
          'relative z-[52] mx-auto hidden w-full max-w-xl items-center justify-between rounded-md border border-neutral-800 bg-neutral-900 p-4 shadow-xl md:flex',
          {
            'rounded-br-none': isMenuOpen || addon,
          },
        )}>
        <div className="w-[128px] md:w-[212px]">
          <Link href="/">
            <Image
              src={HivemapperLogo}
              alt="Hivemapper Logo"
              className="size-full"
            />
          </Link>
        </div>
        <nav className="hidden md:flex">
          <div className="flex gap-2">
            {navLinks.map(link => {
              const isSelected = router.asPath.includes(link.href);
              return (
                <Link key={link.href} href={link.href}>
                  <Button
                    className={cn(
                      'px-4 py-3 text-base font-normal text-neutral-0',
                      {
                        'bg-azure-700 hover:bg-azure-700': isSelected,
                      },
                    )}
                    variant={link.variant ?? 'darkLight'}>
                    {link.label}
                  </Button>
                </Link>
              );
            })}
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="darkLight">
                  <DoubleChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="bottom"
                sideOffset={17}
                align="end"
                alignOffset={-17}
                className="z-[100] w-[290px] rounded-t-none border-neutral-800 text-base">
                {menuLinks.map(link => (
                  <DropdownMenuItem
                    asChild
                    className="border-b border-neutral-800 outline-none"
                    key={link.label}>
                    <Link
                      className="size-full p-4 text-base"
                      target={link.newTab ? '_blank' : '_self'}
                      href={link.href}>
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuGroup className="flex w-full">
                  <DropdownMenuItem
                    asChild
                    className="size-full p-4 outline-none">
                    <Link
                      className="flex justify-between text-base"
                      target="_blank"
                      href={socialUrls.x}>
                      Follow Us
                      <XIcon className="size-4 fill-azure-400" />
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
      </header>
      <MobileHeader isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} />
      {addon ? addon : null}
    </div>
  );
}

const MobileHeader = ({ isNavOpen, setIsNavOpen }: MobileHeaderProps) => {
  const router = useRouter();

  const mobileNavSections: NavSection[] = [
    {
      title: 'Metrics',
      items: [
        {
          label: 'Coverage',
          href: routes.coverage,
        },
        {
          label: 'Contributors',
          href: routes.network.contributors,
        },
        {
          label: 'Regions',
          href: routes.network.regions,
        },
      ],
    },
    {
      title: 'Network',
      items: [
        {
          label: 'Documentation',
          href: externalUrls.hivemapperDocs,
          newTab: true,
        },
        {
          label: 'Blog',
          href: externalUrls.mediumBlog,
          newTab: true,
        },
      ],
    },
  ];

  const socialSection: NavSection = {
    title: 'Social',
    items: [
      {
        label: 'Follow',
        href: socialUrls.x,
        icon: <XIcon className="size-4 fill-azure-400" />,
      },
    ],
  };

  return (
    <>
      <Dialog open={isNavOpen}>
        <DialogOverlay />
      </Dialog>
      <HeaderContainer
        isNavOpen={isNavOpen}
        closeNav={() => setIsNavOpen(false)}
        className={cn(
          'relative z-[55] mx-auto flex w-full grow flex-col items-center justify-between gap-4 overflow-hidden rounded-md border border-neutral-800 bg-neutral-900 px-4 py-2 shadow-xl md:hidden',
        )}>
        <div className="flex w-full justify-between">
          <div className="w-[132px] md:w-[212px]">
            <Link href="/">
              <Image
                src={HivemapperLogo}
                alt="Hivemapper Logo"
                className="size-full"
              />
            </Link>
          </div>
          <button
            className="size-6"
            onClick={() => setIsNavOpen(prev => !prev)}>
            <HamburgerIcon
              className="size-full fill-neutral-0"
              isMenuOpen={isNavOpen}
            />
          </button>
        </div>

        {isNavOpen ? (
          <nav className="flex w-full flex-1 grow flex-col justify-between pt-2">
            <div className="flex flex-col gap-6">
              {mobileNavSections.map(section => (
                <div key={section.title} className="flex flex-col gap-2">
                  <P className="text-xs font-semibold text-neutral-500">
                    {section.title}
                  </P>
                  <ul className="flex flex-col">
                    {section.items.map(item => {
                      const isSelected = router.asPath.includes(item.href);
                      return (
                        <li key={item.href} className="py-2 leading-none">
                          <Link
                            target={item.newTab ? '_blank' : '_self'}
                            href={item.href}
                            className={cn(
                              'font-monserrat text-base font-semibold leading-none text-neutral-0 transition-colors active:text-neutral-500',
                              {
                                'text-azure-400': isSelected,
                              },
                            )}>
                            {item.label}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
            <div className="flex grow flex-col justify-end">
              <P className="py-4 text-xs font-semibold text-neutral-500">
                {socialSection.title}
              </P>
              <ul className="flex w-full justify-stretch gap-3">
                {socialSection.items.map(item => (
                  <li key={item.href} className="w-full">
                    <Link
                      href={item.href}
                      className="flex flex-col items-center justify-between gap-3 rounded-lg border border-neutral-700 bg-neutral-800 px-2 py-3 font-monserrat text-base font-normal text-neutral-0 hover:bg-neutral-800">
                      {item.icon}
                      <P>{item.label}</P>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        ) : null}
      </HeaderContainer>
    </>
  );
};
